"use client";

import { useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { AnimatePresence, motion } from "framer-motion";
import type { Locale } from "@/config/i18n";
import { CHAUFFEUR_SLUG_UI } from "./chauffeurSlugUi";
import { tr } from "@/config/localeText";

type FaqItem = {
  q: string;
  a: string;
};

type SlugFaqSectionProps = {
  heading: string;
  items: FaqItem[];
  locale?: Locale;
};

export default function SlugFaqSection({
  heading,
  items,
  locale = "fr",
}: SlugFaqSectionProps): JSX.Element {
  const [open, setOpen] = useState<number>(0);
  const ui = tr(CHAUFFEUR_SLUG_UI, locale);

  if (!items?.length) return <></>;

  return (
    <section className="bg-white py-16 text-black">
      <div className="mx-auto max-w-[90rem] px-6 md:px-8">
        <motion.p
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.4 }}
          className="mb-2 text-xs uppercase tracking-[0.18em] text-black/55"
        >
          {ui.faqEyebrow}
        </motion.p>
        <motion.h2
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.45 }}
          className="mb-5 font-space-age text-xl tracking-[0.2em]"
        >
          {heading}
        </motion.h2>
        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr] lg:items-start">
          <div className="space-y-3">
            {items.map((item, index) => {
              const isOpen = open === index;
              return (
                <motion.article
                  key={item.q}
                  layout
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10%" }}
                  transition={{ duration: 0.35, delay: index * 0.04 }}
                  whileHover={{ y: -2 }}
                  className="rounded-2xl border border-black/10 bg-black/[0.02] transition hover:border-gold-500/35"
                >
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setOpen(isOpen ? -1 : index)}
                    className="flex w-full items-center justify-between gap-3 px-5 py-4 text-start"
                  >
                    <span className="text-sm font-semibold text-black">
                      {item.q}
                    </span>
                    <motion.span
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-gold-400/40 bg-gold-100/40 text-gold-700"
                    >
                      <Icon icon="mdi:chevron-down" className="h-4 w-4" />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false} mode="wait">
                    {isOpen && (
                      <motion.div
                        key="faq-content"
                        layout
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                          height: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
                          opacity: { duration: 0.2, ease: "easeOut" },
                        }}
                        className="overflow-hidden border-t border-black/10"
                      >
                        <motion.p
                          initial={false}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ y: -4, opacity: 0 }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                          className="px-5 py-4 text-sm text-black/80"
                        >
                          {item.a}
                        </motion.p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.article>
              );
            })}
          </div>
          <motion.aside
            initial={false}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.45 }}
            whileHover={{ y: -3 }}
            className="rounded-2xl border border-black/15 bg-black/[0.03] p-6 lg:sticky lg:top-28"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-gold-800">
              {ui.faqAsideEyebrow}
            </p>
            <h3 className="mt-3 text-lg font-semibold">{ui.faqAsideTitle}</h3>
            <p className="mt-2 text-sm text-black/75">{ui.faqAsideText}</p>
            <div className="mt-4 rounded-xl border border-black/10 bg-white px-4 py-3 text-xs text-black/65">
              {ui.faqAsideNote}
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
